import Phaser from 'phaser';
import { aabbOverlap } from '../utils/MathUtils';
import { createStone, updateStone, stoneOverlap } from '../logic/stone-logic';
import type { IStoneState } from '../logic/stone-logic';
import type { EnemyLogic } from '../logic/enemy-logic';
import type { ScoreSystem } from './ScoreSystem';
import type { SoundManager } from '../audio/SoundManager';
import type { EffectSystem } from './EffectSystem';
import type { PlayerLogic } from '../logic/player-logic';
import { CONSTANTS } from '../constants';

export interface ICombatSystem {
  spawnStone(playerState: { x: number; y: number; facing: 'left' | 'right' }, _playerLogic: PlayerLogic, scene: Phaser.Scene): void;
  checkStoneEnemyCollision(
    enemies: Array<{ logic: EnemyLogic; sprite: Phaser.GameObjects.Sprite; stoneKillable?: boolean }>,
    score: ScoreSystem,
    effectSystem: EffectSystem,
    soundManager: SoundManager,
    _playerLogic: PlayerLogic,
    scene: Phaser.Scene,
    scoreStoneKill: number
  ): void;
  checkStomp(
    playerState: { vy: number },
    enemies: Array<{ logic: EnemyLogic; sprite: Phaser.GameObjects.Sprite }>,
    _playerLogic: PlayerLogic,
    score: ScoreSystem,
    effectSystem: EffectSystem,
    soundManager: SoundManager,
    scoreStomp: number,
    _scene: Phaser.Scene
  ): void;
  checkStoneNpcCollision(
    npcs: Array<{ npc: import('../entities/Npc').Npc; sprite: Phaser.GameObjects.Sprite }>,
    score: ScoreSystem,
    effectSystem: EffectSystem,
    _playerLogic: PlayerLogic
  ): void;
  updateVisualStones(delta: number, _scene: Phaser.Scene, _playerLogic: PlayerLogic): void;
  getStones(): Array<{ logic: IStoneState; sprite: Phaser.GameObjects.Sprite }>;
}

export class CombatSystem implements ICombatSystem {
  private stones: Array<{ logic: IStoneState; sprite: Phaser.GameObjects.Sprite }> = [];

  spawnStone(playerState: { x: number; y: number; facing: 'left' | 'right' }, _playerLogic: PlayerLogic, scene: Phaser.Scene): void {
    // _playerLogic parameter is part of the interface contract for future use
    void _playerLogic;
    const stone = createStone(playerState.x, playerState.y - 100, playerState.facing);

    // Try to reuse an inactive stone
    let visualStone: { logic: IStoneState; sprite: Phaser.GameObjects.Sprite } | null = null;
    for (const s of this.stones) {
      if (!s.logic.active) {
        visualStone = s;
        break;
      }
    }

    if (visualStone) {
      visualStone.logic = stone;
      visualStone.sprite.setVisible(true);
    } else {
      const newStone: { logic: IStoneState; sprite: Phaser.GameObjects.Sprite } = {
        logic: stone,
        sprite: scene.add.sprite(stone.x, stone.y, 'stone').setScale(1.5).setOrigin(0.5),
      };
      this.stones.push(newStone);
      visualStone = newStone;
    }

    visualStone!.sprite.x = stone.x;
    visualStone!.sprite.y = stone.y;
    visualStone!.sprite.setVisible(true);
  }

  checkStoneEnemyCollision(
    enemies: Array<{ logic: EnemyLogic; sprite: Phaser.GameObjects.Sprite; stoneKillable?: boolean }>,
    score: ScoreSystem,
    effectSystem: EffectSystem,
    soundManager: SoundManager,
    _playerLogic: PlayerLogic,
    _scene: Phaser.Scene,
    scoreStoneKill: number
  ): void {
    void _scene;
    const stones = this.stones;
    for (const e of enemies) {
      const eState = e.logic.getState();
      if (eState.state === 'dead') continue;
      if (e.stoneKillable === false) continue; // stone passes through

      // Hitbox extends to the visible sprite height (stones fly at head height)
      const hb = e.logic.getHitbox();
      const imgMode = (e.sprite as any)._enemyImageMode === true;
      const visH = imgMode && e.sprite.displayHeight > hb.height
        ? e.sprite.displayHeight * 0.85
        : hb.height;
      const enemyHitbox = { x: hb.x, y: eState.y - visH, width: hb.width, height: visH };

      for (const st of stones) {
        if (!st.logic.active) continue;

        if (stoneOverlap(st.logic, enemyHitbox)) {
          st.logic.active = false;
          st.sprite.setVisible(false);
          _playerLogic.removeStone();

          const killed = e.logic.takeDamage(1);
          effectSystem.spawnParticles(e.sprite.x, eState.y - visH / 2, 5, 0xff4444);
          effectSystem.screenShake(0.05, 200);
          soundManager.playHit();

          if (killed) {
            score.addScore(scoreStoneKill);
            effectSystem.spawnParticles(e.sprite.x, eState.y - visH / 2, 10, 0xffaa00);
            e.sprite.setVisible(false); // death feedback: enemy disappears
          }
        }
      }
    }
  }

  checkStomp(
    playerState: { vy: number },
    enemies: Array<{ logic: EnemyLogic; sprite: Phaser.GameObjects.Sprite }>,
    _playerLogic: PlayerLogic,
    score: ScoreSystem,
    effectSystem: EffectSystem,
    soundManager: SoundManager,
    scoreStomp: number,
    _scene: Phaser.Scene
  ): void {
    void _scene;
    for (const e of enemies) {
      const eState = e.logic.getState();
      if (eState.state === 'dead' || eState.state === 'hurt') continue;

      const playerHitbox = _playerLogic.getHitbox();
      const enemyHitbox = e.logic.getHitbox();
      if (!aabbOverlap(playerHitbox, enemyHitbox)) continue;

      const playerBottom = playerHitbox.y + playerHitbox.height;
      const enemyTop = enemyHitbox.y;
      const enemyCenterY = enemyHitbox.y + enemyHitbox.height / 2;
      const playerCenterY = playerHitbox.y + playerHitbox.height / 2;

      if (playerState.vy > 0 && playerBottom >= enemyTop && playerCenterY >= enemyCenterY) {
        const killed = e.logic.takeDamage(1);
        _playerLogic.setState({ vy: CONSTANTS.JUMP_VELOCITY * 0.7 });
        soundManager.playJump();
        effectSystem.screenShake(0.03, 150);

        if (killed) {
          score.addScore(scoreStomp);
          effectSystem.spawnParticles(e.sprite.x, e.sprite.y - e.sprite.height / 2, 10, 0xffaa00);
          e.sprite.setVisible(false); // death feedback: enemy disappears
        }
      }
    }
  }

  checkStoneNpcCollision(
    npcs: Array<{ npc: import('../entities/Npc').Npc; sprite: Phaser.GameObjects.Sprite; stoneKillable?: boolean }>,
    _score: ScoreSystem,
    effectSystem: EffectSystem,
    _playerLogic: PlayerLogic
  ): void {
    void _score;
    const stones = this.stones;
    for (const n of npcs) {
      if (n.npc.hasBeenCollected()) continue;
      if (n.stoneKillable === false) continue; // stone passes through
      // Hitbox extends to visible height so stones flying at head height connect
      const hb = n.npc.getHitbox();
      const visH = n.sprite.displayHeight > hb.height
        ? n.sprite.displayHeight * 0.85
        : hb.height;
      const npcHitbox = { x: hb.x, y: n.npc.y - visH, width: hb.width, height: visH };
      for (const st of stones) {
        if (!st.logic.active) continue;
        if (stoneOverlap(st.logic, npcHitbox)) {
          st.logic.active = false;
          st.sprite.setVisible(false);
          _playerLogic.removeStone();
          n.npc.markKilled();
          effectSystem.spawnParticles(n.sprite.x, n.sprite.y - n.npc.height / 2, 10, 0xffff00);
        }
      }
    }
  }

  updateVisualStones(delta: number, _scene: Phaser.Scene, _playerLogic: PlayerLogic): void {
    void _scene;
    for (const s of this.stones) {
      if (!s.logic.active) {
        s.sprite.setVisible(false);
        continue;
      }

      const wasActive = s.logic.active;
      updateStone(s.logic, delta);
      s.sprite.x = s.logic.x;
      s.sprite.y = s.logic.y;
      s.sprite.setRotation(s.logic.rotation);

      if (wasActive && !s.logic.active) {
        // Stone expired without hitting anything: release the activeStones slot
        _playerLogic.removeStone();
        s.sprite.setVisible(false);
      }
    }
  }

  getStones(): Array<{ logic: IStoneState; sprite: Phaser.GameObjects.Sprite }> {
    return this.stones;
  }
}
